import React, { useMemo } from "react";
import type { RouteComponentProps } from "@gatsbyjs/reach-router";
import { useRaceMeetingWithRaces } from "@/hooks/firestore/v2/betting/useRaceMeetings";
import { PageMessage } from "@/components/PageMessage";
import { ButtonGroup } from "components/ButtonGroup";
import { LinkButton } from "@/components/LinkButton";
import { useIsMobile } from "@/hooks/useDisplay";
import { PermissionsRequired } from "library";
import { RaceMeetingSkeleton } from "./RaceMeetingSkeleton";
import { Page } from "@/components/Page";
import { Box } from "@/components/Box";

// TODO: migrate these 2 to @/features/ folder once racing is fully refactored
import EventBlocked from "sections/Betting/components/EventBlocked/EventBlocked";
import { RaceMeetingDropDown } from "sections/Betting/components/RaceMeetingDropDown";

import { ReactComponent as RaceError } from "components/assets/racing-error.svg";
import { RaceMeetingHeader } from "@/features/racing/components/RaceMeetingHeader";
import { RaceMeetingHeaderSkeleton } from "@/features/racing/components/RaceMeetingHeaderSkeleton";
import { MeetingRaceRow } from "@/features/racing/components/MeetingRaceRow";

type RaceMeetingProps = {
  id?: string;
} & RouteComponentProps;

const statusPriority = [
  "OPEN",
  "JUMPED",
  "INTERIM",
  "PROTEST",
  "FINAL",
  "ABANDONED",
];

const RaceMeeting = ({ id }: RaceMeetingProps) => {
  const { meeting, races, isLoading } = useRaceMeetingWithRaces(id);
  const isMobile = useIsMobile();
  const notFound = !isLoading && !meeting?.visible;

  const sortedRaces = useMemo(() => {
    if (!races) return;

    return races.sort((a, b) => {
      const priorityA = statusPriority.includes(a.status)
        ? statusPriority.indexOf(a.status)
        : 99;

      const priorityB = statusPriority.includes(b.status)
        ? statusPriority.indexOf(b.status)
        : 99;

      return priorityA - priorityB;
    });
  }, [races]);

  if (notFound) {
    return (
      <PageMessage
        title="Race Meeting not found"
        image={<RaceError />}
        subTitle="There are currently no race meetings which match your filters"
      >
        <ButtonGroup>
          <LinkButton
            variant={`primary`}
            size={isMobile ? `md` : `default`}
            to="/racing/betting/"
          >
            Next to Jump
          </LinkButton>
        </ButtonGroup>
      </PageMessage>
    );
  }

  return (
    <EventBlocked filters={meeting?.filters || []} loading={isLoading}>
      <PermissionsRequired
        permissionsRequired={[
          "viewRacingMarkets",
          "viewBettingMarkets",
          ...(meeting?.region === "ANZ" || isLoading
            ? []
            : ["submitInternationalRacingBet"]),
        ]}
        skeleton={<RaceMeetingSkeleton />}
      >
        <Page
          variant={`full`}
          title={
            meeting ? (
              <RaceMeetingHeader meeting={meeting} />
            ) : (
              <RaceMeetingHeaderSkeleton />
            )
          }
        >
          {isLoading || !sortedRaces ? (
            <RaceMeetingSkeleton />
          ) : (
            <Box hideHeader noChildrenSpace>
              {sortedRaces.map((race) => (
                <MeetingRaceRow key={`meeting-race-${race.id}`} event={race} />
              ))}
            </Box>
          )}
        </Page>
      </PermissionsRequired>
    </EventBlocked>
  );
};

export { RaceMeeting };
